import React, { useState, useEffect, useCallback } from 'react';
import {
  Target, Plus, RefreshCw, ChevronDown, ChevronUp, Loader2,
  CheckCircle, AlertTriangle, Clock, Pause, Play, RotateCcw,
  Building2, Calendar, TrendingUp, X, Search
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './CoverageDashboard.css';

const STATUS_COLORS = {
  complete: '#22c55e',
  pending: '#64748b',
  queued: '#3b82f6',
  in_progress: '#3b82f6',
  needs_subdivision: '#f59e0b',
  gaps_detected: '#f97316',
  failed: '#ef4444',
  skipped: '#94a3b8',
};

const STATUS_LABELS = {
  complete: 'Complete',
  pending: 'Pending',
  queued: 'Queued',
  in_progress: 'Running',
  needs_subdivision: 'Subdivided',
  gaps_detected: 'Gaps',
  failed: 'Failed',
  skipped: 'Skipped',
};

const ACQ_TYPES = [
  'IT Goods',
  'IT Services',
  'Non-IT Goods',
  'Non-IT Services',
];

function CoverageDashboard({ departmentCode = null }) {
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [windows, setWindows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCampaign, setExpandedCampaign] = useState(null);

  // Create campaign form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    department_codes: '',
    date_from: '2021-01-01',
    date_to: new Date().toISOString().split('T')[0],
    acquisition_types: ['IT Goods'],
  });
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/coverage/dashboard');
      setCampaigns(data.campaigns || []);
      setStats(data.stats || null);
      setError(null);
    } catch (err) {
      console.error('Coverage fetch error:', err);
      setError(err.message || 'Failed to load coverage data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const fetchWindows = async (campaignId) => {
    try {
      const data = await apiClient.get(`/api/coverage/campaigns/${campaignId}/windows`);
      setWindows(data.windows || []);
    } catch (err) {
      console.error('Windows fetch error:', err);
    }
  };

  const handleToggleCampaign = (id) => {
    if (expandedCampaign === id) {
      setExpandedCampaign(null);
      setWindows([]);
    } else {
      setExpandedCampaign(id);
      fetchWindows(id);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const deptCodes = createForm.department_codes
        .split(',')
        .map(d => d.trim())
        .filter(Boolean);
      await apiClient.post('/api/coverage/campaigns', {
        name: createForm.name,
        department_codes: deptCodes,
        acquisition_types: createForm.acquisition_types,
        date_from: createForm.date_from,
        date_to: createForm.date_to,
      });
      setShowCreate(false);
      setCreateForm({ name: '', department_codes: '', date_from: '2021-01-01', date_to: new Date().toISOString().split('T')[0], acquisition_types: ['IT Goods'] });
      fetchData();
    } catch (err) {
      alert('Failed to create campaign: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  const handlePauseCampaign = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    await apiClient.put(`/api/coverage/campaigns/${id}`, { status: newStatus });
    fetchData();
  };

  const handleRetryWindow = async (windowId) => {
    await apiClient.post(`/api/coverage/retry/${windowId}`);
    if (expandedCampaign) fetchWindows(expandedCampaign);
    fetchData();
  };

  const handleRetryAllFailed = async () => {
    await apiClient.post('/api/coverage/retry-all-failed');
    fetchData();
  };

  // Group windows by month for heatmap
  const windowsByMonth = windows.reduce((acc, w) => {
    const key = w.date_from?.substring(0, 7) || 'unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(w);
    return acc;
  }, {});

  // Filter campaigns to selected department
  const visibleCampaigns = departmentCode
    ? campaigns.filter(c => c.department_codes?.includes(departmentCode))
    : campaigns;

  if (loading && !stats) {
    return <div className="cov-loading"><Loader2 size={20} className="cov-spin" /> Loading coverage data...</div>;
  }

  return (
    <div className="cov-container">
      {/* Header */}
      <div className="cov-header">
        <div className="cov-header-left">
          <Target size={18} />
          <h3>Coverage Tracking</h3>
        </div>
        <div className="cov-header-right">
          <button className="cov-btn cov-btn-ghost" onClick={fetchData}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="cov-btn cov-btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> New Campaign
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="cov-error">
          <AlertTriangle size={14} />
          {error}
          <button className="cov-btn cov-btn-ghost" onClick={fetchData} style={{ marginLeft: 'auto', padding: '0.2rem 0.5rem' }}>
            Retry
          </button>
        </div>
      )}

      {/* Stats Bar */}
      {stats && (
        <div className="cov-stats-bar">
          <div className="cov-stat">
            <span className="cov-stat-value">{stats.total_captured?.toLocaleString() || 0}</span>
            <span className="cov-stat-label">POs Captured</span>
          </div>
          <div className="cov-stat">
            <span className="cov-stat-value">{stats.unique_pos?.toLocaleString() || 0}</span>
            <span className="cov-stat-label">Unique POs</span>
          </div>
          <div className="cov-stat">
            <span className="cov-stat-value">{stats.completed_windows || 0}/{stats.total_windows || 0}</span>
            <span className="cov-stat-label">Windows Done</span>
          </div>
          <div className="cov-stat">
            <span className="cov-stat-value">{stats.pending_windows || 0}</span>
            <span className="cov-stat-label">Pending</span>
          </div>
          {(stats.failed_windows > 0) && (
            <div className="cov-stat cov-stat-alert">
              <span className="cov-stat-value">{stats.failed_windows}</span>
              <span className="cov-stat-label">Failed</span>
              <button className="cov-retry-link" onClick={handleRetryAllFailed}>Retry all</button>
            </div>
          )}
          <div className="cov-stat">
            <span className="cov-stat-value">{stats.detail_queue_pending || 0}</span>
            <span className="cov-stat-label">Details Queued</span>
          </div>
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreate && (
        <div className="cov-modal-backdrop" onClick={() => setShowCreate(false)}>
          <div className="cov-modal" onClick={e => e.stopPropagation()}>
            <div className="cov-modal-header">
              <h3>Create Scrape Campaign</h3>
              <button className="cov-close" onClick={() => setShowCreate(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleCreateCampaign}>
              <div className="cov-field">
                <label>Campaign Name</label>
                <input
                  value={createForm.name}
                  onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. CAL FIRE IT Goods 2021-2026"
                  required
                />
              </div>
              <div className="cov-field">
                <label>Department Codes (comma-separated)</label>
                <input
                  value={createForm.department_codes}
                  onChange={e => setCreateForm(f => ({ ...f, department_codes: e.target.value }))}
                  placeholder="e.g. 3540, 7730, 2660"
                  required
                />
                <span className="cov-hint">Find codes at caleprocure.ca.gov or in the departments table</span>
              </div>
              <div className="cov-field-row">
                <div className="cov-field">
                  <label>From</label>
                  <input type="date" value={createForm.date_from}
                    onChange={e => setCreateForm(f => ({ ...f, date_from: e.target.value }))} required />
                </div>
                <div className="cov-field">
                  <label>To</label>
                  <input type="date" value={createForm.date_to}
                    onChange={e => setCreateForm(f => ({ ...f, date_to: e.target.value }))} required />
                </div>
              </div>
              <div className="cov-field">
                <label>Acquisition Types</label>
                <div className="cov-acq-types">
                  {ACQ_TYPES.map(t => (
                    <label key={t} className="cov-checkbox-label">
                      <input
                        type="checkbox"
                        checked={createForm.acquisition_types.includes(t)}
                        onChange={e => {
                          setCreateForm(f => ({
                            ...f,
                            acquisition_types: e.target.checked
                              ? [...f.acquisition_types, t]
                              : f.acquisition_types.filter(x => x !== t),
                          }));
                        }}
                      />
                      {t}
                    </label>
                  ))}
                </div>
                {createForm.acquisition_types.length === 0 && (
                  <span className="cov-hint" style={{ color: '#ef4444' }}>Select at least one type</span>
                )}
              </div>
              <div className="cov-modal-actions">
                <button type="button" className="cov-btn cov-btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="cov-btn cov-btn-primary" disabled={creating || createForm.acquisition_types.length === 0}>
                  {creating ? <><Loader2 size={14} className="cov-spin" /> Creating...</> : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Campaign List */}
      {visibleCampaigns.length === 0 ? (
        <div className="cov-empty">
          <Target size={32} />
          <p>{departmentCode ? 'No campaigns for this department' : 'No campaigns yet'}</p>
          <span>Create a campaign to start tracking procurement coverage across departments and time periods.</span>
        </div>
      ) : (
        <div className="cov-campaign-list">
          {visibleCampaigns.map(c => {
            const isExpanded = expandedCampaign === c.id;
            const pct = c.total_expected > 0
              ? Math.round(c.total_captured / c.total_expected * 100)
              : (c.completed_windows > 0 ? 100 : 0);

            return (
              <div key={c.id} className={`cov-campaign ${isExpanded ? 'cov-expanded' : ''}`}>
                <div className="cov-campaign-header" onClick={() => handleToggleCampaign(c.id)}>
                  <div className="cov-campaign-info">
                    <div className="cov-campaign-name">
                      <Building2 size={16} />
                      {c.name}
                      <span className={`cov-campaign-status cov-status-${c.status}`}>{c.status}</span>
                    </div>
                    <div className="cov-campaign-meta">
                      <span><Calendar size={12} /> {c.date_from} → {c.date_to}</span>
                      <span>Depts: {c.department_codes?.join(', ')}</span>
                      {c.acquisition_types && <span>Types: {c.acquisition_types.join(', ')}</span>}
                      <span>{c.completed_windows}/{c.total_windows} windows</span>
                    </div>
                  </div>
                  <div className="cov-campaign-progress">
                    <div className="cov-progress-bar">
                      <div className="cov-progress-fill" style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                    <span className="cov-progress-label">
                      {c.total_captured?.toLocaleString()} / {c.total_expected?.toLocaleString() || '?'} POs
                    </span>
                  </div>
                  <div className="cov-campaign-actions">
                    <button
                      className="cov-btn cov-btn-icon"
                      onClick={(e) => { e.stopPropagation(); handlePauseCampaign(c.id, c.status); }}
                      title={c.status === 'active' ? 'Pause' : 'Resume'}
                    >
                      {c.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                    </button>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>

                {/* Coverage Heatmap */}
                {isExpanded && (
                  <div className="cov-heatmap">
                    {Object.keys(windowsByMonth).length === 0 ? (
                      <div className="cov-loading"><Loader2 size={16} className="cov-spin" /> Loading windows...</div>
                    ) : (
                      <div className="cov-heatmap-grid">
                        {Object.entries(windowsByMonth).sort(([a], [b]) => a.localeCompare(b)).map(([month, wins]) => {
                          // For subdivided windows, show children instead of parent
                          const leafWindows = wins.filter(w => w.status !== 'needs_subdivision');
                          const totalExpected = leafWindows.reduce((s, w) => s + (w.expected_count || 0), 0);
                          const totalActual = leafWindows.reduce((s, w) => s + (w.actual_count || 0), 0);
                          const allComplete = leafWindows.every(w => w.status === 'complete');
                          const hasFailed = leafWindows.some(w => w.status === 'failed');
                          const hasPending = leafWindows.some(w => w.status === 'pending' || w.status === 'queued');

                          const cellStatus = allComplete ? 'complete'
                            : hasFailed ? 'failed'
                            : hasPending ? 'pending'
                            : 'in_progress';

                          return (
                            <div
                              key={month}
                              className={`cov-heatmap-cell cov-cell-${cellStatus}`}
                              title={`${month}: ${totalActual}${totalExpected ? ` / ${totalExpected}` : ''} POs | ${leafWindows.length} window(s)`}
                            >
                              <span className="cov-cell-month">{month.substring(5)}</span>
                              <span className="cov-cell-count">{totalActual || '-'}</span>
                              {hasFailed && (
                                <button
                                  className="cov-cell-retry"
                                  onClick={() => leafWindows.filter(w => w.status === 'failed').forEach(w => handleRetryWindow(w.id))}
                                  title="Retry failed"
                                >
                                  <RotateCcw size={10} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Legend */}
                    <div className="cov-legend">
                      {['complete', 'pending', 'in_progress', 'failed', 'needs_subdivision'].map(s => (
                        <span key={s} className="cov-legend-item">
                          <span className="cov-legend-dot" style={{ background: STATUS_COLORS[s] }} />
                          {STATUS_LABELS[s]}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CoverageDashboard;
